import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../api/http';
import WideModal from '../components/WideModal';
import FileManagerSelectModal from '../components/FileManagerSelectModal';
import { buildMinioProxyUrl, openWithAuth } from '../utils/authedResource';

const toDateRangeLabel = (plan) => {
  const start = plan?.training_start_date ? String(plan.training_start_date).slice(0, 10) : '';
  const end = plan?.training_end_date ? String(plan.training_end_date).slice(0, 10) : '';
  if (start && end) return `${start} ~ ${end}`;
  if (start) return start;
  if (end) return end;
  return '—';
};

const clampPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, num));
};

const participantStatusLabel = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return '已完成';
  if (s === 'pending' || !s) return '未完成';
  return status;
};

const buildPlanForm = (plan) => ({
  topic: plan?.topic || '',
  department_names: plan?.department_names || '',
  content: plan?.content || '',
  material_object: plan?.material_object || plan?.attachment_object || '',
  material_name: plan?.material_name || plan?.attachment_name || '',
  material_mime: plan?.material_mime || '',
  method: plan?.method || '',
  training_start_date: plan?.training_start_date ? String(plan.training_start_date).slice(0, 10) : '',
  training_end_date: plan?.training_end_date ? String(plan.training_end_date).slice(0, 10) : '',
  training_hours: plan?.training_hours ?? '',
  status: plan?.status || 'draft'
});

export default function TrainingPlansTab({ addToast }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [planForm, setPlanForm] = useState(buildPlanForm(null));
  const [materialSelectOpen, setMaterialSelectOpen] = useState(false);

  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
  const [participantsPlan, setParticipantsPlan] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantRows, setParticipantRows] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(() => new Set());
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeDepartment, setEmployeeDepartment] = useState('');

  const sectionTitleStyle = { fontSize: '1.1rem', fontWeight: 600, margin: '0 0 .75rem', color: 'var(--text-primary)' };
  const hintStyle = { fontSize: 12, color: 'var(--text-secondary)', marginTop: '.75rem', lineHeight: 1.5 };
  const actionsBarStyle = { display: 'flex', gap: '.75rem', marginBottom: '1rem', flexWrap: 'wrap' };
  const btn = (variant = 'primary') => ({
    padding: '.55rem 1rem',
    fontSize: 13,
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 500,
    border: '1px solid',
    background: variant === 'primary' ? 'var(--brand-primary)' : 'var(--bg-tertiary)',
    color: variant === 'primary' ? '#fff' : 'var(--text-primary)',
    borderColor: variant === 'primary' ? 'var(--brand-primary)' : 'var(--border-color)',
    transition: 'background .2s, color .2s, border-color .2s'
  });

  const cardStyle = {
    background: '#fff',
    border: '1px solid var(--border-color)',
    borderRadius: 12,
    padding: '12px 14px',
    boxShadow: '0 6px 16px rgba(15,23,42,0.05)'
  };

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get('/education-training/training-plans');
      setPlans(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('[training-plans-tab] fetch plans failed:', err);
      addToast?.(err.response?.data?.message || '获取培训计划失败', 'error');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const openCreatePlan = () => {
    setEditingPlan(null);
    setPlanForm(buildPlanForm(null));
    setPlanModalOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm(buildPlanForm(plan));
    setPlanModalOpen(true);
  };

  const closePlanModal = () => {
    setPlanModalOpen(false);
    setEditingPlan(null);
    setPlanSubmitting(false);
  };

  const handlePlanFormChange = (e) => {
    const { name, value } = e.target;
    setPlanForm((prev) => ({ ...prev, [name]: value }));
  };

  const openSelectedMaterial = useCallback(async () => {
    const objectName = String(planForm.material_object || '').trim();
    const name = String(planForm.material_name || '').trim();
    if (!objectName) {
      addToast?.('请先选择教材文件', 'warning');
      return;
    }
    const url = buildMinioProxyUrl(objectName, { filename: name || undefined });
    if (!url) {
      addToast?.('无法打开该教材文件', 'error');
      return;
    }
    await openWithAuth(url);
  }, [addToast, planForm.material_name, planForm.material_object]);

  const clearSelectedMaterial = () => {
    setPlanForm((prev) => ({
      ...prev,
      material_object: '',
      material_name: '',
      material_mime: ''
    }));
  };

  const submitPlan = async (e) => {
    e?.preventDefault?.();
    if (!planForm.topic || !String(planForm.topic).trim()) {
      addToast?.('培训主题为必填项', 'error');
      return;
    }
    setPlanSubmitting(true);
    try {
      const topic = String(planForm.topic).trim();
      const training_hours = planForm.training_hours === '' ? null : Number(planForm.training_hours);
      const jsonPayload = {
        ...planForm,
        topic,
        training_hours,
        material_object: planForm.material_object ? String(planForm.material_object).trim() : null,
        material_name: planForm.material_name ? String(planForm.material_name).trim() : null,
        material_mime: planForm.material_mime ? String(planForm.material_mime).trim() : null
      };

      if (editingPlan?.id) {
        await http.put(`/education-training/training-plans/${editingPlan.id}`, jsonPayload);
        addToast?.('培训计划已更新', 'success');
      } else {
        await http.post('/education-training/training-plans', jsonPayload);
        addToast?.('培训计划已创建', 'success');
      }
      await fetchPlans();
      closePlanModal();
    } catch (err) {
      console.error('[training-plans-tab] submit plan failed:', err);
      if (err?.response?.status === 413) {
        addToast?.('请求体过大或服务器网关限制（413）。请减少提交内容或联系管理员调整反向代理限制。', 'error');
      } else {
        addToast?.(err.response?.data?.message || '保存失败', 'error');
      }
    } finally {
      setPlanSubmitting(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!plan?.id) return;
    const ok = window.confirm(`确认删除培训计划“${plan.topic}”？该计划下参培人员与完成记录将一并删除。`);
    if (!ok) return;
    try {
      await http.delete(`/education-training/training-plans/${plan.id}`);
      addToast?.('已删除', 'success');
      fetchPlans();
    } catch (err) {
      console.error('[training-plans-tab] delete plan failed:', err);
      addToast?.(err.response?.data?.message || '删除失败', 'error');
    }
  };

  const fetchEmployeesIfNeeded = useCallback(async () => {
    if (employeesLoading) return;
    if (employees.length > 0) return;
    setEmployeesLoading(true);
    try {
      const res = await http.get('/education-training/training-plans/employees');
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('[training-plans-tab] fetch employees failed:', err);
      addToast?.(err.response?.data?.message || '获取员工列表失败', 'error');
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, [addToast, employees.length, employeesLoading]);

  const departmentOptions = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      const dep = e?.department ? String(e.department).trim() : '';
      if (dep) set.add(dep);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'));
  }, [employees]);

  const openParticipantsModal = async (plan) => {
    setParticipantsPlan(plan);
    setParticipantsModalOpen(true);
    setEmployeeQuery('');
    setEmployeeDepartment('');
    await fetchEmployeesIfNeeded();

    setParticipantsLoading(true);
    try {
      const res = await http.get(`/education-training/training-plans/${plan.id}/participants`);
      const rows = Array.isArray(res.data) ? res.data : [];
      setParticipantRows(rows);
      setSelectedEmployeeIds(new Set(rows.map((r) => r.employee_id).filter(Boolean)));
    } catch (err) {
      console.error('[training-plans-tab] fetch participants failed:', err);
      addToast?.(err.response?.data?.message || '获取参培人员失败', 'error');
      setParticipantRows([]);
      setSelectedEmployeeIds(new Set());
    } finally {
      setParticipantsLoading(false);
    }
  };

  const closeParticipantsModal = () => {
    setParticipantsModalOpen(false);
    setParticipantsPlan(null);
    setParticipantRows([]);
    setSelectedEmployeeIds(new Set());
    setParticipantsLoading(false);
  };

  const toggleEmployee = (empId) => {
    setSelectedEmployeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) next.delete(empId);
      else next.add(empId);
      return next;
    });
  };

  const saveParticipants = async () => {
    if (!participantsPlan?.id) return;
    try {
      await http.post(`/education-training/training-plans/${participantsPlan.id}/participants`, {
        employee_ids: Array.from(selectedEmployeeIds)
      });
      addToast?.('参培人员已保存', 'success');
      // refresh
      const res = await http.get(`/education-training/training-plans/${participantsPlan.id}/participants`);
      const rows = Array.isArray(res.data) ? res.data : [];
      setParticipantRows(rows);
      setSelectedEmployeeIds(new Set(rows.map((r) => r.employee_id).filter(Boolean)));
      fetchPlans();
    } catch (err) {
      console.error('[training-plans-tab] save participants failed:', err);
      addToast?.(err.response?.data?.message || '保存参培人员失败', 'error');
    }
  };

  const filteredEmployees = useMemo(() => {
    const q = String(employeeQuery || '').toLowerCase();
    const departmentFilter = String(employeeDepartment || '').trim();
    return employees.filter((e) => {
      if (departmentFilter && String(e?.department || '').trim() !== departmentFilter) return false;
      if (!q) return true;
      const name = String(e?.name || '').toLowerCase();
      const departmentText = String(e?.department || '').toLowerCase();
      const pos = String(e?.position || '').toLowerCase();
      return name.includes(q) || departmentText.includes(q) || pos.includes(q);
    });
  }, [employeeQuery, employeeDepartment, employees]);

  const renderPlanProgress = (plan) => {
    const total = Number(plan?.participant_count || 0);
    const completed = Number(plan?.completed_count || 0);
    const percent = total > 0 ? clampPercent((completed / total) * 100) : 0;

    return (
      <div style={{ minWidth: 160 }}>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
          完成 {completed}/{total}（{total > 0 ? `${percent.toFixed(percent % 1 === 0 ? 0 : 1)}%` : '--'}）
        </div>
        <div style={{ height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
          <div style={{ width: `${percent}%`, height: '100%', background: 'var(--brand-primary)' }} />
        </div>
      </div>
    );
  };

  return (
    <div>
      <h2 style={sectionTitleStyle}>培训计划</h2>
      <div style={actionsBarStyle}>
        <button style={btn('primary')} onClick={openCreatePlan}>新增培训计划</button>
        <button style={btn('secondary')} onClick={fetchPlans} disabled={loading}>{loading ? '刷新中...' : '刷新'}</button>
      </div>

      {loading ? (
        <div>正在加载...</div>
      ) : plans.length === 0 ? (
        <div style={{ ...cardStyle, background: '#f8fafc', borderStyle: 'dashed', color: '#475569' }}>
          暂无培训计划。可先新增计划并关联参培员工，再到“岗中教育培训台账”登记学时/成绩。
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
          {plans.map((plan) => (
            <div key={plan.id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>{plan.topic}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                    时间：{toDateRangeLabel(plan)} · 学时：{plan.training_hours ?? '—'} · 方式：{plan.method || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                    参培部门/范围：{plan.department_names || '—'}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                  {renderPlanProgress(plan)}
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>状态：{plan.status || 'draft'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <button style={btn('secondary')} onClick={() => openParticipantsModal(plan)}>参培人员 & 完成情况</button>
                <button style={btn('secondary')} onClick={() => openEditPlan(plan)}>编辑</button>
                <button style={{ ...btn('secondary'), borderColor: '#fecaca', color: '#991b1b', background: '#fff1f2' }} onClick={() => deletePlan(plan)}>删除</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p style={hintStyle}>说明：本页用于创建计划并关联参培员工，完成登记请前往“岗中教育培训台账”。</p>

      <WideModal
        isOpen={planModalOpen}
        onClose={closePlanModal}
        title={editingPlan ? '编辑培训计划' : '新增培训计划'}
        size="wide"
      >
        <form onSubmit={submitPlan} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>培训主题 *</div>
              <input name="topic" value={planForm.topic} onChange={handlePlanFormChange} required style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8 }} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>培训方式</div>
              <input name="method" value={planForm.method} onChange={handlePlanFormChange} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8 }} placeholder="如：线下授课/线上/实操" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>开始日期</div>
              <input type="date" name="training_start_date" value={planForm.training_start_date} onChange={handlePlanFormChange} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8 }} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>结束日期</div>
              <input type="date" name="training_end_date" value={planForm.training_end_date} onChange={handlePlanFormChange} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8 }} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>学时</div>
              <input type="number" step="0.5" name="training_hours" value={planForm.training_hours} onChange={handlePlanFormChange} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8 }} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>状态</div>
              <select name="status" value={planForm.status} onChange={handlePlanFormChange} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8, background: '#fff' }}>
                <option value="draft">draft</option>
                <option value="published">published</option>
                <option value="archived">archived</option>
              </select>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#64748b' }}>参培部门/人员范围（文本）</div>
            <input
              name="department_names"
              list="training-plan-departments"
              value={planForm.department_names}
              onChange={handlePlanFormChange}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8 }}
              placeholder="输入可检索部门；多部门可用逗号分隔"
            />
            <datalist id="training-plan-departments">
              {departmentOptions.map((dep) => (
                <option key={dep} value={dep} />
              ))}
            </datalist>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>培训教材（从文件管理选择，可选）</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" style={btn('secondary')} onClick={() => setMaterialSelectOpen(true)}>
                从文件管理选择
              </button>
              {planForm.material_object ? (
                <>
                  <button type="button" style={btn('secondary')} onClick={openSelectedMaterial}>打开</button>
                  <button type="button" style={btn('secondary')} onClick={clearSelectedMaterial}>清除</button>
                </>
              ) : null}
            </div>
            {planForm.material_object ? (
              <div style={{ marginTop: 8, color: 'var(--text-secondary)' }}>
                已选择：{planForm.material_name || planForm.material_object}
              </div>
            ) : editingPlan?.attachment_url ? (
              <div style={{ marginTop: 8 }}>
                <a href={editingPlan.attachment_url} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-primary)' }}>
                  查看历史教材（旧：上传 PDF）
                </a>
              </div>
            ) : null}
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#64748b' }}>培训内容</div>
            <textarea name="content" value={planForm.content} onChange={handlePlanFormChange} rows={5} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" style={btn('secondary')} onClick={closePlanModal} disabled={planSubmitting}>取消</button>
            <button type="submit" style={btn('primary')} disabled={planSubmitting}>{planSubmitting ? '保存中...' : '保存'}</button>
          </div>
        </form>
      </WideModal>

      <FileManagerSelectModal
        isOpen={materialSelectOpen}
        onClose={() => setMaterialSelectOpen(false)}
        addToast={addToast}
        initialPrefix="培训资料"
        allowedKinds={['pdf', 'ppt', 'video']}
        onSelect={(file) => {
          setPlanForm((prev) => ({
            ...prev,
            material_object: file?.objectName || '',
            material_name: file?.fileName || '',
            material_mime: file?.mimeType || ''
          }));
        }}
      />

      <WideModal
        isOpen={participantsModalOpen}
        onClose={closeParticipantsModal}
        title={participantsPlan ? `参培人员 - ${participantsPlan.topic}` : '参培人员'}
        size="wide"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ ...cardStyle, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 700, color: '#0f172a' }}>选择参培员工</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <select
                  value={employeeDepartment}
                  onChange={(e) => setEmployeeDepartment(e.target.value)}
                  style={{ width: 200, padding: '7px 10px', border: '1px solid var(--border-color)', borderRadius: 8, background: '#fff' }}
                  disabled={employeesLoading}
                >
                  <option value="">全部部门</option>
                  {departmentOptions.map((dep) => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </select>
                <input
                  value={employeeQuery}
                  onChange={(e) => setEmployeeQuery(e.target.value)}
                  placeholder="搜索姓名/部门/岗位"
                  style={{ width: 220, padding: '7px 10px', border: '1px solid var(--border-color)', borderRadius: 8 }}
                />
                <button style={btn('primary')} onClick={saveParticipants} disabled={participantsLoading || employeesLoading}>
                  保存参培人员
                </button>
              </div>
            </div>
            <div style={{ marginTop: 10, maxHeight: 280, overflow: 'auto', borderTop: '1px solid var(--border-color)', paddingTop: 10 }}>
              {employeesLoading ? (
                <div>正在加载员工...</div>
              ) : filteredEmployees.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 13 }}>暂无可选员工</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 8 }}>
                  {filteredEmployees.map((emp) => {
                    const checked = selectedEmployeeIds.has(emp.id);
                    return (
                      <label key={emp.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: '1px solid var(--border-color)', borderRadius: 10, padding: '8px 10px', background: checked ? '#eff6ff' : '#fff' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleEmployee(emp.id)} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>{emp.name}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{emp.department || '—'} · {emp.position || '—'}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>已选：{selectedEmployeeIds.size} 人</div>
          </div>

          <div style={{ ...cardStyle, padding: 12 }}>
            <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>完成情况预览</div>
            {participantsLoading ? (
              <div>正在加载参培人员...</div>
            ) : participantRows.length === 0 ? (
              <div style={{ fontSize: 13, color: '#64748b' }}>尚未关联参培员工</div>
            ) : (
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>姓名</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>部门</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>岗位</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>学时</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>成绩</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>状态</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>完成日期</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participantRows.map((row) => (
                      <tr key={row.id}>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.employee_name || '—'}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.department || '—'}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.position || '—'}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.completed_hours ?? '—'}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.score ?? '—'}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{participantStatusLabel(row.status)}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{row.completed_at ? String(row.completed_at).slice(0, 10) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </WideModal>
    </div>
  );
}
